// The onboarding form's rules — core.md §4.3, field for field.
//
// Pure and React-free, so both surfaces and tests/screens.test.jsx read the same
// validation. The two surfaces differ in chrome (a card on a desktop, a full
// scroll on a phone); they do not differ in what a valid answer is.
//
// ── One step, no wizard ───────────────────────────────────────────────────
//
// A student has nothing to resolve beyond who they are: no clinic to join, no
// licence to verify, no specialty tree to walk. The doctor app's multi-step
// registration exists because a doctor's answers CONDITION each other; none of
// these do, except `field`, which is shown only for a resident.

import { LEVELS } from '../../constants/vocabulary.js';
import { foldDigits } from '../../lib/caseForm.js';

export { LEVELS };

/** Jalali years the form accepts. A student who entered before 1370 is not one. */
export const MIN_ENTRY_YEAR = 1370;
export const MAX_ENTRY_YEAR = 1420;

/**
 * The student number, as it goes on the wire.
 *
 * Persian and Arabic-Indic digits are folded to ASCII FIRST: a student typing
 * «۹۸۱۲۳۴۵۶» on a Persian keyboard has entered a perfectly good student number,
 * and rejecting it for the script it was typed in would be the app failing at
 * the first field of the first screen. The store normalises again on its side
 * (`student_number_norm`); this is what makes the value the student SEES match
 * the one that was validated.
 */
export const normalizeStudentNumber = (value) => foldDigits(String(value ?? '')).trim();

/** core.md §4.3: letters, digits and dashes, 4–20 of them. */
export const STUDENT_NUMBER_RE = /^[A-Za-z0-9-]{4,20}$/;

/** `field` is a residency programme, so it exists only for a resident. */
export const showsField = (level) => level === 'resident';

/** Every field a string, because that is what an input holds. */
export const emptyOnboardingForm = (suggestedFullName = '') => ({
  fullName: suggestedFullName || '',
  studentNumber: '',
  university: '',
  level: '',
  field: '',
  entryYear: '',
  bio: '',
});

const len = (v) => String(v ?? '').trim().length;

/**
 * Field → i18n key of what is wrong with it, or null.
 *
 * Keyed by FIELD NAME rather than returned as a list, because that is also the
 * shape `extensions.field` arrives in when the BFF rejects the submission
 * (`INVALID_INPUT`, core.md §4.3): one map, filled either locally or from the
 * server, and one rendering path in the surfaces.
 */
export function validateOnboarding(form) {
  const errors = {};

  const nameLen = len(form?.fullName);
  if (nameLen < 2 || nameLen > 120) errors.fullName = 'onboarding.validation.fullName';

  const number = normalizeStudentNumber(form?.studentNumber);
  if (!STUDENT_NUMBER_RE.test(number)) errors.studentNumber = 'onboarding.validation.studentNumber';

  const uniLen = len(form?.university);
  if (uniLen < 2 || uniLen > 120) errors.university = 'onboarding.validation.university';

  if (!LEVELS.includes(form?.level)) errors.level = 'onboarding.validation.level';

  // Only validated when it is SHOWN. A student who picked «دستیار», typed a
  // specialty, then changed to «کارورز» must not be blocked by a field that is
  // no longer on the screen.
  if (showsField(form?.level) && len(form?.field) > 80) errors.field = 'onboarding.validation.field';

  const year = parseEntryYear(form?.entryYear);
  if (year === false) errors.entryYear = 'onboarding.validation.entryYear';

  if (len(form?.bio) > 500) errors.bio = 'onboarding.validation.bio';

  return errors;
}

/**
 * `null` when blank (the field is optional), the integer when it is a Jalali
 * year in range, and `false` when it is neither — three answers, because
 * "absent" and "wrong" are different outcomes and only one of them is an error.
 */
export function parseEntryYear(value) {
  const raw = foldDigits(String(value ?? '')).trim();
  if (!raw) return null;
  if (!/^\d{4}$/.test(raw)) return false;
  const n = Number(raw);
  if (n < MIN_ENTRY_YEAR || n > MAX_ENTRY_YEAR) return false;
  return n;
}

export const isOnboardingValid = (form) => Object.keys(validateOnboarding(form)).length === 0;

/**
 * What goes under a field the BFF rejected with `INVALID_INPUT`.
 *
 * The errors map holds an i18n KEY per field when the rule was checked locally.
 * When the BFF rejected the field and said why, its sentence is more useful
 * than the catalogue's generic «معتبر نیست» — so that case is a `{ message }`
 * object, which `OnboardingFields` renders verbatim instead of through `t()`.
 * The transport's own placeholder (`BFF error: 400`) is not a sentence and
 * falls back to the key.
 */
export function fieldError(err) {
  const message = typeof err?.message === 'string' ? err.message.trim() : '';
  return message && !/^BFF error: \d+$/.test(message) ? { message } : 'errors.invalidInput';
}

/** Render one entry of the errors map — a key through `t`, a `{ message }` as is. */
export const fieldErrorText = (entry, t) => {
  if (!entry) return '';
  if (typeof entry === 'object') return entry.message || '';
  return t(entry);
};

/**
 * Form → `StudentOnboardingInput`.
 *
 * Blank optional fields are OMITTED rather than sent as `''`: an empty string
 * stored where the student wrote nothing is a value the profile header would go
 * on to render as a fact. `field` is omitted for a non-resident even if
 * something was typed before the level changed.
 */
export function toOnboardingInput(form) {
  const input = {
    fullName: String(form.fullName).trim(),
    studentNumber: normalizeStudentNumber(form.studentNumber),
    university: String(form.university).trim(),
    level: form.level,
  };
  if (showsField(form.level)) {
    const field = String(form.field ?? '').trim();
    if (field) input.field = field;
  }
  const year = parseEntryYear(form.entryYear);
  if (year) input.entryYear = year;
  const bio = String(form.bio ?? '').trim();
  if (bio) input.bio = bio;
  return input;
}

/**
 * Form → `StudentProfileUpdateInput`, for the EDIT path on `/profile`.
 *
 * The same seven fields as `toOnboardingInput`, with one difference the
 * contract forces (core.md §4.3): on `updateStudentProfile` an omitted field is
 * UNTOUCHED and `""` is what clears one. So a blank optional field is omitted
 * only when the stored profile had nothing there either; when the student
 * emptied a `field`, an `entryYear` or a `bio` that previously held a value,
 * the clearing is sent explicitly — `""` for the strings and `0` for the year
 * (an `Int` with no empty string to send).
 *
 * `field` also clears when the level moved off `resident`: the specialty is a
 * residency programme, and a profile that keeps «داخلی» beside «کارورز» is
 * showing a fact the student just said is not true.
 *
 * @param form      the form values
 * @param previous  the stored `StudentProfile` the form was opened from
 */
export function toProfileUpdateInput(form, previous) {
  const input = toOnboardingInput(form);
  const prev = previous || {};
  if (!('field' in input) && String(prev.field ?? '').trim()) input.field = '';
  if (!('entryYear' in input) && prev.entryYear) input.entryYear = 0;
  if (!('bio' in input) && String(prev.bio ?? '').trim()) input.bio = '';
  return input;
}

/**
 * A `StudentProfile` back into form values — the profile screen edits the same
 * fields through the same rules.
 */
export function profileToForm(profile) {
  const src = profile || {};
  return {
    fullName: src.fullName || '',
    studentNumber: src.studentNumber || '',
    university: src.university || '',
    level: LEVELS.includes(src.level) ? src.level : '',
    field: src.field || '',
    entryYear: src.entryYear ? String(src.entryYear) : '',
    bio: src.bio || '',
  };
}

/** The four stages of core.md §4.3, in order. */
export const STAGES = ['gate', 'form', 'saving', 'done'];
